// World-view variant of the mobile DOM gallery: the FULL archive (~250 items)
// pan/zoomable on a phone, including iOS.
//
// Why this can work where rounds 1–6 failed: both iOS killers are avoided at
// once. At far zoom the world is drawn by ~16 permanently-mounted strip <img>s
// (one pre-baked WebP per collection row, composited server-side from the
// 60x0 thumbs by scripts/generate-strips.js) — so the "everything visible"
// regime needs no per-item DOM and no decode burst. Zoomed in past the mount
// gate, the base class's windowed per-item machinery takes over — the exact
// code already proven on iOS at single-collection scale — and it never has
// more than ~a viewport of items mounted because the gate guarantees items
// are ≥ ~80 screen px before any mount happens.
//
// Strips never unmount. Zoom-out is therefore always instantly safe: per-item
// DOM is shed (bounded per frame) while identical pixels remain underneath.
//
// Routed behind ?worldview=1 (session-persistent) — see init() in
// gallery-animate-zoom.js. The stable single-collection path is the untouched
// base class.

import { GalleryAnimateZoomDOM, ROW_GAP_FOCUS, ROW_GAP_SOFTNESS } from './gallery-animate-zoom-dom.js';
import { createOverlay } from './perf-overlay.js';
import { fetchStripManifest, stripEntryFor, stripUrl } from './strips.js';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { VIEW_ALL_ZOOM, viewAllInsetX, fitCollectionView } from './gallery-virt.js';

// Must match gallery-animate-zoom-dom.js layout constants.
const ITEM_W = 220;

// Mount gate with hysteresis: per-item <img>s mount only at/above MOUNT_ZOOM
// (item ≈ 99 screen px). Keeping the handoff above the 80px thumbnail-tier
// boundary reduces the visible item count and avoids two transitions at once.
// They are shed below UNMOUNT_ZOOM. The dead band stops churn when a pinch
// settles right on the boundary.
const MOUNT_ZOOM   = 0.45;
const UNMOUNT_ZOOM = 0.38;

// Hard ceiling on simultaneously-mounted items, whatever the viewport math
// says. ~30 fit a phone viewport at MOUNT_ZOOM; 150 is a generous safety
// valve, far below the ~250-at-once burst that killed iOS.
const MOUNTED_MAX = 150;

class GalleryWorldview extends GalleryAnimateZoomDOM {
    constructor() {
        super();
        if (!this.viewport) return;
        this._maxMountMoving = 0;          // pans/flings are compositor-only: zero DOM churn mid-gesture
        this._maxMountSettled = 6;         // spread the strip-to-item handoff over several frames
        this._mountedMax     = MOUNTED_MAX;
        this._gateOpen       = false;
        this._shedTimer      = null;
    }

    destroy() {
        if (this._shedTimer) { clearTimeout(this._shedTimer); this._shedTimer = null; }
        super.destroy();
    }

    async init() {
        // Same shape as the base init, plus the strip manifest fetched in
        // parallel with the records. buildLayout needs both.
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        const manifestP = fetchStripManifest();
        await this.loadData();
        this._stripManifest = await manifestP; // null → every row falls back to tint mosaic
        this.buildLayout();
        this.bindEvents();
        this.checkUrlParam();
        this._syncWorld();
        this._wake();

        this._statsOn = true;
        this._stats   = this._stats || {};
        if (new URLSearchParams(location.search).has('perf')) createOverlay(this);
    }

    // ── Layout: full world, no collection scoping ─────────────────────────────

    _sectionsForBuild(typeItems, keyFor) {
        this._buildCollectionList(typeItems, keyFor); // filter popout becomes a jump list
        this._activeFilter = 'all';                   // every item visible/hit-testable
        return [{ items: typeItems }];
    }

    _applyInitialView(isAccessory) {
        this._world.classList.add('is-worldview'); // scoped label/strip styles
        const fitZ = this.worldH > 0 ? this.H / this.worldH : 1;
        this.fitZ = fitZ;
        // Whole world visible at start, a touch outside fit zoom and inset from
        // the left so the row labels (which hang off each row's left edge) are
        // in frame. The floor sits below the landing zoom so there's still room
        // to pinch further out.
        this.ZOOM_MIN = Math.min(this.ZOOM_MIN, fitZ * 0.8);
        this.zoom = Math.max(this.ZOOM_MIN, fitZ * VIEW_ALL_ZOOM);
        this.panX = viewAllInsetX(this.W);
        this.panY = (this.H - this.worldH * this.zoom) / 2;
        this.labelFullZ = Math.min(fitZ * 4, this.ZOOM_MAX);
        this.ZOOM_LEVELS = [
            fitZ,
            MOUNT_ZOOM * 1.2,
            this.labelFullZ,
            Math.min(fitZ * 10, this.ZOOM_MAX),
            this.ZOOM_MAX,
        ].sort((a, b) => a - b).filter((z, i, a) => i === 0 || z > a[i - 1] * 1.2);
        void isAccessory;
    }

    // ── Strip layer ───────────────────────────────────────────────────────────

    _afterGroupBuilt(g) {
        const stripEl = document.createElement('div');
        stripEl.className = 'gallery-dom-strip';
        stripEl.style.cssText = `left:0;top:0;width:${g.width}px;height:${g.rowH}px;`;

        // Instant fallback: an N×1 canvas, one blurhash-tint pixel per item,
        // stretched across the row. Zero network, always layout-aligned.
        const canvas = document.createElement('canvas');
        canvas.width  = g.items.length;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        g.items.forEach((item, i) => {
            ctx.fillStyle = this._bhTintFor(item);
            ctx.fillRect(i, 0, 1, 1);
        });
        stripEl.style.backgroundImage  = `url(${canvas.toDataURL()})`;
        stripEl.style.backgroundSize   = '100% 100%';
        stripEl.style.imageRendering   = 'pixelated'; // crisp per-item cells, not a smear

        // Baked strip, if the manifest has a row that still matches.
        const type  = this.viewport.dataset.filter || 'look';
        const entry = stripEntryFor(this._stripManifest, type, g);
        if (entry) {
            const img = document.createElement('img');
            img.className = 'gallery-dom-strip-img';
            img.decoding  = 'async';
            img.draggable = false;
            // Keep the compositor texture at the baked strip's intrinsic size
            // (60px per item) and scale it to world units. Giving the <img> the
            // full ~9,460px row width made 3x phones cross an 8,192px layer/tile
            // boundary around zoom 0.29, matching the real-device hitch/flicker.
            const cellW      = this._stripManifest?.cellW || 60;
            const itemW      = this._stripManifest?.itemW || ITEM_W;
            const stripScale = itemW / cellW;
            const stripW     = entry.count * cellW;
            const stripH     = Math.max(1, Math.round((entry.rowHWorld || g.rowH) / stripScale));
            img.style.width           = `${stripW}px`;
            img.style.height          = `${stripH}px`;
            img.style.transformOrigin = '0 0';
            img.style.transform       = `scale(${stripScale})`;
            img.src = stripUrl(entry);
            const show = () => {
                img.classList.add('is-loaded');
                // The tint mosaic is only a loading fallback. Leaving its data
                // background stretched across the full row keeps an unnecessary
                // giant paint source underneath the real strip forever.
                const clearFallback = () => { stripEl.style.backgroundImage = 'none'; };
                img.addEventListener('transitionend', clearFallback, { once: true });
                setTimeout(clearFallback, 350);
            };
            if (img.decode) img.decode().then(show).catch(() => {});
            else img.onload = show;
            stripEl.appendChild(img);
            g._hasStrip = true; // mounted frames/wrappers can go transparent
        }

        // Insert BEFORE items/labels so per-item imgs stack above the strip.
        g.el.insertBefore(stripEl, g.el.firstChild);
        g._stripEl = stripEl;
        g._labelEl = g.el.querySelector('.gallery-dom-label');
    }

    // ── Mount smoothing ───────────────────────────────────────────────────────
    // The base mounts with an opaque blurhash-tint background and an instantly
    // visible <img> — right for the single-collection view (nothing behind it),
    // wrong here: the strip already renders this exact cell, so any opaque
    // layer covering it reads as a flash. Make the wrapper AND the accessory
    // white frame transparent (the strip's baked cell already includes the
    // white frame), and fade the first <img> in once it's decoded.
    //
    // The fade is applied INLINE and removed after it completes — a blanket
    // CSS transition made every tier swap crossfade both layers, whose summed
    // opacity dips mid-fade and lets the background pulse through (the
    // accessories "flicker"). First mount fades; tier swaps stay atomic.

    _mountItem(item) {
        super._mountItem(item);
        if (!item._el) return;
        item._el.style.backgroundColor = 'transparent';
        if (item._frameEl && item._group?._hasStrip) {
            item._frameEl.style.background = 'transparent';
        }
        const img = item._curImg;
        if (img && img.src) {
            img.classList.remove('is-current'); // start hidden, fade in on decode
            img.style.transition = 'opacity 0.25s ease';
            const show = () => {
                if (item._curImg !== img || !item._el) return;
                img.classList.add('is-current');
                setTimeout(() => { img.style.transition = ''; }, 300);
            };
            if (img.decode) img.decode().then(show).catch(show);
            else img.onload = show;
        }
    }

    // Spotlight gap, rebased for the world view. The base ramps it in from fit
    // zoom — here that would independently translate every long strip row
    // exactly in the far-zoom range, where all ~12 are on screen (each a moving
    // compositor surface; the original iOS perf reason it was disabled). The
    // rows instead hold fixed geometry until the item-mount gate opens — the
    // single world transform stays the only moving layer at far zoom — and the
    // split-apart fades in from there, when only a row or two is visible.
    _groupOffset(g) {
        const startZ = MOUNT_ZOOM;
        const endZ   = this.labelFullZ ?? (this.fitZ ?? this.zoom) * 4;
        const zoomScale = Math.max(0, Math.min(1, (this.zoom - startZ) / (endZ - startZ)));
        if (zoomScale === 0) return 0;
        const focusCenterY = (this.H / 2 - this.panY) / this.zoom;
        const groupCenterY = g.rowY + g.rowH / 2;
        const x = (groupCenterY - focusCenterY) / ROW_GAP_SOFTNESS;
        const s = 1 / (1 + Math.exp(-x));
        return ROW_GAP_FOCUS * zoomScale * (s - 0.5);
    }

    // ── Mount gate (hysteresis) + stats ──────────────────────────────────────

    _updateScene() {
        const gate = this._gateOpen ? UNMOUNT_ZOOM : MOUNT_ZOOM;
        this._gateOpen     = this.zoom >= gate;
        this._mountZoomMin = gate;
        super._updateScene();
        // Below the gate the base sheds ≤30 items per call, but _updateScene
        // only runs when pan/zoom changes — keep shedding on a timer until
        // nothing per-item is left mounted.
        if (!this._gateOpen && this._mounted.size && !this._shedTimer) {
            this._shedTimer = setTimeout(() => {
                this._shedTimer = null;
                if (!this._destroyed) this._updateScene();
            }, 120);
        }
        if (this._statsOn) this._updateStats();
    }

    _updateStats() {
        const s = this._stats || (this._stats = {});
        const now = performance.now();
        if (!this._fpsTimes) this._fpsTimes = [];
        this._fpsTimes.push(now);
        if (this._fpsTimes.length > 60) this._fpsTimes.shift();
        const span = this._fpsTimes.at(-1) - this._fpsTimes[0];
        s.fps     = span > 0 ? Math.round((this._fpsTimes.length - 1) / span * 1000) : '--';
        s.sprites = this._mounted.size;
        s.queue   = this._tierLoadQueue.length;
        s.loading = this._tierLoadActive;
        s.zoom    = this.zoom.toFixed(2);
        s.gate    = this._gateOpen ? 'open' : 'shut';
        const m = performance.memory;
        if (m) s.heapMB = Math.round(m.usedJSHeapSize / 1048576);
    }

    // On-screen test must consider Y too — unlike the single-row base view,
    // the world view is a tall multi-row world.
    _isOnScreen(item) {
        const vl = -this.panX / this.zoom;
        const vr = vl + this.W / this.zoom;
        const vt = -this.panY / this.zoom;
        const vb = vt + this.H / this.zoom;
        const vy = this._itemVisualY(item);
        return item._x < vr && item._x + ITEM_W > vl && vy < vb && vy + item._h > vt;
    }

    // ── Labels: counter-scaled to a constant ~16 screen px ───────────────────

    _syncWorld() {
        super._syncWorld();
        if (!this._world) return;
        // 40px font (see .gallery-dom-label) × k × zoom = 16px on screen.
        const k = Math.min(3, 0.4 / this.zoom);
        if (Math.abs(k - (this._lastLabelK || 0)) > 0.005) {
            this._lastLabelK = k;
            for (const g of this.groups) {
                if (g._labelEl) g._labelEl.style.transform = `scale(${k})`;
            }
        }
    }

    // ── Filter ───────────────────────────────────────────────────────────────
    //
    // The base class is the single-collection fallback: it can only ever show
    // one collection, so its switcher has no "All" and picking an entry rebuilds
    // the layout. The world view has the whole archive laid out already, so it
    // behaves like the desktop canvas instead — "All" is a real destination, and
    // picking a collection genuinely hides the others rather than just jumping
    // to them.

    _renderFilterButtons() {
        if (this._collectionList.length < 2) return;
        const nav = document.getElementById('gallery-filter');
        const active = this._activeFilter === 'all' ? 'all' : this._activeCollection;
        renderFilterButtons(nav, this._collectionList, active, key => {
            this.setFilter(key);
        });
    }

    setFilter(key) {
        if (!key) return;
        const showAll = key === 'all';
        const group   = showAll ? null : this.groups.find(gr => gr.key === key);
        if (!showAll && !group?.items.length) return;

        this._activeFilter     = showAll ? 'all' : key;
        this._activeCollection = showAll ? null : key;
        resetCrumbTracking(this);

        // Hiding the row element takes its strip, label and mounted items with
        // it in one go; _itemVisible then stops the scene mounting any more.
        for (const g of this.groups) {
            g.el.style.display = (showAll || g.key === key) ? '' : 'none';
        }

        if (showAll) this._applyInitialView(false);
        else Object.assign(this, fitCollectionView(this, group));
        this.velX = this.velY = this.zoomVel = 0;

        this._renderFilterButtons();
        this._updateScene();
        this._syncWorld();
        this._wake();

        // Preserve other params (worldview flag, perf, …) — the base version wipes them.
        const params = new URLSearchParams(location.search);
        const label  = this._collectionList.find(c => c.key === key)?.label;
        if (showAll || !label) params.delete('filter');
        else params.set('filter', label.toLowerCase());
        const qs = params.toString();
        history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
    }

    checkUrlParam() {
        // ?filter= applies the filter here (the base scopes it at build time).
        const params      = new URLSearchParams(location.search);
        const filterParam = params.get('filter');
        if (filterParam && filterParam !== 'all') {
            const want = filterParam.toLowerCase();
            const c    = this._collectionList.find(cc => cc.label.toLowerCase() === want);
            if (c) this.setFilter(c.key);
        }
        super.checkUrlParam(); // ?item= deep link
    }
}

export { GalleryWorldview };
